import { json2md, JsonToMarkdownOptions } from '@/lib/json2md';
import { MarkdownFormatter } from '@/lib/json2md';
import { SocialBriefItemType } from '@inference.engine/widgets/web';
import { i18n } from '@/shared/i18n';

export function formatDateToMonthYear(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Example of how to use json2md to convert a social brief to markdown
 * This demonstrates the flexibility of the json2md utility
 * Note: This is a simplified example since the current json2md utility
 * only supports top-level properties, not nested paths
 */

type Json2MdBriefConfig = JsonToMarkdownOptions<SocialBriefItemType>['configs'];
export function briefJson2Markdown(brief: SocialBriefItemType): string {
  // Since the current json2md utility only supports top-level properties,
  // we'll create a flattened version of the brief data

  const configs: Json2MdBriefConfig = [
    {
      property: 'brandName',
      format: (value) =>
        MarkdownFormatter.H1(
          i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.socialBrief', {
            brand: value,
          }),
          1,
        ),
    },
    {
      property: 'overview',
      format: (value) => {
        const sections: string[] = [];

        sections.push(
          MarkdownFormatter.H2(i18n.t('contentTest:socialBriefWidget.overviewSection.overview'), 1),
        );
        sections.push(
          MarkdownFormatter.TEXT(
            `${MarkdownFormatter.BOLD(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.opportunity'),
            )} ${value.opportunityTitle}`,
            1,
          ),
        );
        sections.push(
          MarkdownFormatter.TEXT(
            `${MarkdownFormatter.BOLD(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.opportunityDescription'),
            )} ${value.opportunityDescription}`,
            1,
          ),
        );

        // Trend Summary
        sections.push(
          MarkdownFormatter.H3(
            i18n.t('contentTest:socialBriefWidget.overviewSection.trendSummary'),
          ),
        );
        sections.push(MarkdownFormatter.BOLD(value.trendSummary.title));
        sections.push(
          MarkdownFormatter.ITALIC(
            i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.detected', {
              date: value.trendSummary.dateDetected,
            }),
            1,
          ),
        );
        sections.push(MarkdownFormatter.TEXT(value.trendSummary.description, 1));

        if (value.trendSummary.sourceLinks.length > 0) {
          sections.push(
            MarkdownFormatter.BOLD(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.sources'),
            ),
          );
          sections.push(MarkdownFormatter.BULLET_LIST(value.trendSummary.sourceLinks, 1));
        }

        // Why It Matters
        sections.push(
          MarkdownFormatter.H3(
            i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.whyItMattersToTheBrand'),
          ),
        );
        sections.push(
          MarkdownFormatter.TEXT(
            `${MarkdownFormatter.BOLD(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.brandRelevance'),
            )} ${value.whyMattersToBrand.brandRelevance}`,
            1,
          ),
        );

        return sections.join('\n');
      },
    },
    {
      property: 'customerUnderstanding',
      format: (value) => {
        const sections: string[] = [];

        sections.push(
          MarkdownFormatter.H2(
            i18n.t(
              'contentTest:socialBriefWidget.customerUnderstandingSection.customerUnderstanding',
            ),
            1,
          ),
        );

        if (value.targetAudienceOrDemandSpace.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.targetAudience'),
            ),
          );
          sections.push(MarkdownFormatter.BULLET_LIST(value.targetAudienceOrDemandSpace, 1));
        }

        if (value.culturalContext.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t('contentTest:socialBriefWidget.customerUnderstandingSection.culturalContext'),
            ),
          );
          sections.push(MarkdownFormatter.BULLET_LIST(value.culturalContext, 1));
        }

        return sections.join('\n');
      },
    },
    {
      property: 'brandContext',
      format: (value) => {
        const sections: string[] = [];

        sections.push(
          MarkdownFormatter.H2(
            i18n.t('contentTest:socialBriefWidget.brandContextSection.brandContext'),
            1,
          ),
        );

        // Brand Mentions
        if (value.brandMentions.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.brandMentions'),
            ),
          );
          value.brandMentions.forEach((mention) => {
            sections.push(MarkdownFormatter.BOLD(mention.label));
            sections.push(mention.description);
            if (mention.source) {
              sections.push(
                MarkdownFormatter.ITALIC(
                  i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.source', {
                    source: mention.source,
                  }),
                  1,
                ),
              );
            }
          });
        }

        // Competitive Mentions
        if (value.competitiveMentions.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t('contentTest:socialBriefWidget.brandContextSection.competitiveMentions'),
            ),
          );
          value.competitiveMentions.forEach((mention) => {
            sections.push(MarkdownFormatter.BOLD(mention.label));
            sections.push(mention.description);
            if (mention.source) {
              sections.push(
                MarkdownFormatter.ITALIC(
                  i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.source', {
                    source: mention.source,
                  }),
                  1,
                ),
              );
            }
          });
        }

        // Key Messages
        if (value.keyMessages.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.keyMessages'),
            ),
          );
          value.keyMessages.forEach((message) => {
            sections.push(MarkdownFormatter.BOLD(message.label));
            sections.push(message.description);
            if (message.source) {
              sections.push(
                MarkdownFormatter.ITALIC(
                  i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.source', {
                    source: message.source,
                  }),
                  1,
                ),
              );
            }
          });
        }

        // Tone of Voice
        sections.push(
          MarkdownFormatter.H3(
            i18n.t('contentTest:socialBriefWidget.brandContextSection.toneOfVoice'),
          ),
        );
        if (value.toneOfVoice.styles.length > 0) {
          sections.push(
            MarkdownFormatter.BOLD(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.styles'),
            ),
          );
          sections.push(MarkdownFormatter.BULLET_LIST(value.toneOfVoice.styles, 1));
        }

        if (value.toneOfVoice.creativeGuidance.length > 0) {
          sections.push(
            MarkdownFormatter.BOLD(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.creativeGuidance'),
            ),
          );
          sections.push(MarkdownFormatter.BULLET_LIST(value.toneOfVoice.creativeGuidance, 1));
        }

        return sections.join('\n');
      },
    },
    {
      property: 'measurementActivation',
      format: (value) => {
        const sections: string[] = [];

        sections.push(
          MarkdownFormatter.H2(
            i18n.t(
              'contentTest:socialBriefWidget.measurementAndActivationSection.measurementActivation',
            ),
            1,
          ),
        );

        // Platform Recommendations
        sections.push(
          MarkdownFormatter.H3(
            i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.platformRecommendations'),
          ),
        );
        sections.push(
          `${MarkdownFormatter.BOLD(
            i18n.t('contentTest:socialBriefWidget.measurementAndActivationSection.primary'),
          )} ${value.platformRecommendation.primary}`,
        );
        sections.push(
          MarkdownFormatter.TEXT(
            `${MarkdownFormatter.BOLD(
              i18n.t('contentTest:socialBriefWidget.measurementAndActivationSection.secondary'),
            )} ${value.platformRecommendation.secondary}`,
            1,
          ),
        );

        if (value.platformRecommendation.rationales.length > 0) {
          sections.push(
            MarkdownFormatter.BOLD(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.rationales'),
            ),
          );
          sections.push(MarkdownFormatter.BULLET_LIST(value.platformRecommendation.rationales, 1));
        }

        // Assumptions/Flags
        if (value.assumptionsOrFlags.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.assumptionsFlags'),
            ),
          );
          sections.push(MarkdownFormatter.BULLET_LIST(value.assumptionsOrFlags, 1));
        }

        // Asset Needs
        if (value.assetTypeNeeds.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.assetTypeNeeds'),
            ),
          );
          sections.push(MarkdownFormatter.BULLET_LIST(value.assetTypeNeeds, 1));
        }

        return sections.join('\n');
      },
    },
    {
      property: 'creativeBriefSnapshot',
      format: (value) => {
        const sections: string[] = [];

        sections.push(
          MarkdownFormatter.H2(
            i18n.t(
              'contentTest:socialBriefWidget.creativeBriefSnapshotSection.creativeBriefSnapshot',
            ),
            1,
          ),
        );

        // Messaging Starters
        if (value.suggestedMessagingStarters.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t(
                'contentTest:socialBriefWidget.creativeBriefSnapshotSection.suggestedMessagingStarters',
              ),
            ),
          );
          sections.push(MarkdownFormatter.NUMBERED_LIST(value.suggestedMessagingStarters, 1));
        }

        // Recommended Formats
        if (value.recommendedFormats.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t(
                'contentTest:socialBriefWidget.creativeBriefSnapshotSection.recommendedFormats',
              ),
            ),
          );
          sections.push(MarkdownFormatter.BULLET_LIST(value.recommendedFormats, 1));
        }

        // References
        if (value.references.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.references'),
            ),
          );
          sections.push(MarkdownFormatter.BULLET_LIST(value.references, 1));
        }

        // Citation Tags
        if (value.citationTags.length > 0) {
          sections.push(
            MarkdownFormatter.H3(
              i18n.t('contentTest:socialBriefWidget.socialBriefWidgetUtils.citationTags'),
            ),
          );
          const citationLinks = value.citationTags.map((tag) => ({
            label: tag.label,
            url: tag.url,
          }));
          sections.push(
            MarkdownFormatter.BULLET_LIST(citationLinks.map((tag) => `[${tag.label}](${tag.url})`)),
          );
        }

        return sections.join('\n');
      },
    },
  ];

  return json2md(brief, { configs });
}

// ... existing code ...
